import {
  HubCardData,
  HubCardJSONResponse,
  HubCardResponse,
} from '@/hooks/insights-hub/types';
import { HubCardType } from '@/types';
import { parseCardDataJson } from './update-alert-cache';

/**
 * Type guard for HubCardData. Accepts the data from useWrappedQuery and the type from response.cardType to check against.
 * Returns true if the data is of the type specified.
 * @param item - HubCardData
 * @param type - HubCardType
 * @returns true if the data is of the type specified.
 */
export const isHubCardType = <T extends HubCardType>(
  item: HubCardData,
  type: T,
): item is Extract<HubCardData, { card_type: T }> => {
  return item.card_type === type;
};

/**
 * Type guard for HubCardResponse. Accepts the data from useWrappedQuery and the type from response.cardType to check against.
 * @param data - HubCardResponse
 * @param type - HubCardType
 * @returns true if the data is of the type specified.
 */
export const isHubCardResponseOfType = <T extends HubCardType>(
  data: HubCardResponse,
  type: T,
): data is Extract<HubCardResponse, { cardType: T }> => {
  return data.cardType === type;
};

/**
 * Parses the JSON response from the hub card query and returns the card data and type.
 * @param dataJson - HubCardJSONResponse
 * @returns null if the JSON is invalid, otherwise generic HubCardResponse.
 */
export const parseHubCardJSONResponse = (
  dataJson: HubCardJSONResponse,
): HubCardResponse | null => {
  try {
    return {
      cardData: parseCardDataJson(dataJson.cardDataJson),
      cardType: dataJson.cardType,
    } as HubCardResponse;
  } catch (e) {
    return null;
  }
};

/**
 * Casts the JSON response from the hub card query to the expected type.
 * @param dataJson - HubCardJSONResponse
 * @param cardType - HubCardType
 * @returns undefined if the JSON is invalid, otherwise the expected hub card type.
 */
export const castHubCardJSONResponse = <T extends HubCardType>(
  dataJson: HubCardJSONResponse,
  cardType: T,
): Extract<HubCardResponse, { cardType: T }> | undefined => {
  const data = parseHubCardJSONResponse(dataJson);

  return data && isHubCardResponseOfType(data, cardType)
    ? (data as Extract<HubCardResponse, { cardType: T }>)
    : undefined;
};
